// Core React Imports
import { memo } from "react";
import { createPortal } from "react-dom";

// Local Imports
import { DivColumn, DivRow, ParagraphText, WidgetMarginHeight, WidgetMarginWidth } from "../../../styles/SharedStyles";
import ConstantsImages from "../../../constants/ConstantsImages";
import WidgetIconFour from "../../icons/widget-icon-four/WidgetIconFour";
import WidgetIconFive from "../../icons/widget-icon-five/WidgetIconFive";
import WidgetFixedSizeList from "../../shared_widgets/widget-fixed-size-list/WidgetFixedSizeList";
import WidgetCheckbox from "../../checkboxes/WidgetCheckbox";
import WidgetRadioButton from "../../radio-buttons/WidgetRadioButton";
import WidgetRequiredLabel from "../../shared_widgets/widget-required-label/WidgetRequiredLabel";
import { useWidgetDropdown } from "./useWidgetDropdown";

// CSS/Stylesheets
import "./WidgetDropdown.css";

// Dropdown Type Enum
export const DropdownType = {
  MULTI_SELECT: "MULTI_SELECT",
  SINGLE_SELECT: "SINGLE_SELECT",
  DEFAULT_SELECT: "DEFAULT_SELECT",
};

const parseFrozenModelVersionDisplay = (display) => {
  if (!display) {
    return { label: "", version: "", date: "" };
  }

  const match = String(display).match(/^מודל\s+(\d+)\s*(.*)$/);
  if (!match) {
    return { label: display, version: "", date: "" };
  }

  return {
    label: "מודל",
    version: String(Number(match[1])),
    date: match[2]?.trim() || "",
  };
};

const WidgetDropdown = ({
  options,
  onSelect,
  labelKey,
  title,
  selectedOptionProp,
  titleColorProp = "#333333",
  textColorProp = "#373734",
  backgroundColorProp = "#fff",
  heightProp = "auto",
  widthProp = "100%",
  borderRadiusProp = "1.5rem",
  fontSizeTitle = "1.4rem",
  fontWeightTitle = "700",
  fontSizeText = "1.6rem",
  fontWeightText = "900",
  fontFamilyText = "Almoni DL AAA",
  lineHeightText = "1.6rem",
  textAlignText,
  fontFamilyOption = "Almoni DL AAA",
  paddingProp = "0.5rem 1rem 0.5rem 1rem",
  imageProp = null,
  arrowImage = ConstantsImages.ARROW_IMAGE,
  style = {},
  enableSearch = false,
  enableClear = false,
  onClear,
  disabled = false,
  customFooterItemFirst = null,
  onCustomItemClickFirst = null,
  customFooterItemSecond = null,
  onCustomItemClickSecond = null,
  type = DropdownType.DEFAULT_SELECT,
  enableDelete = false,
  onDelete,
  usePortal = false,
  isRequiredUI = false,
  enableFrozenOptionDisplay = false,
}) => {
  const {
    isOpen,
    selectedOption,
    selectedOptions,
    searchTerm,
    setSearchTerm,
    inputRef,
    listRef,
    handleSelect,
    handleMultiSelect,
    handleClear,
    filteredOptions,
    dropdownRef,
    getDisabledTextStyles,
    getDisabledStyles,
    getDisabledClickHandler,
    getArrowIconDimensions,
    getMultiSelectDisplayText,
    isOptionSelected,
    handleOptionClick,
    shouldShowDeleteButton,
    portalPosition,
    isPortalReady,
  } = useWidgetDropdown({
    options,
    onSelect,
    labelKey,
    selectedOptionProp,
    onClear,
    type,
    disabled,
    arrowImage,
    enableDelete,
    usePortal,
  });

  // Render search input when dropdown is open and search is enabled
  const renderSearchInput = () => (
    <input
      ref={inputRef}
      type="text"
      className="dropdown-search-input"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      style={{
        color: textColorProp,
        fontWeight: fontWeightText,
        fontFamily: fontFamilyText,
      }}
    />
  );

  const renderFrozenOptionContent = (display) => {
    const { label, version, date } = parseFrozenModelVersionDisplay(display);

    return (
      <DivRow className="dropdown-frozen-option-row">
        <DivRow className="dropdown-frozen-option-text">
          {label && <span className="dropdown-frozen-model-version-label">{label}</span>}
          {version && <span className="dropdown-frozen-model-version-number">{version}</span>}
          {date && <span className="dropdown-frozen-model-version-date">{date}</span>}
        </DivRow>
        <span className="dropdown-frozen-option-icon">
          <WidgetIconFive iconProp={ConstantsImages.FROZEN_VERSION_BADGE_IMAGE} widthIconProp="1.4rem" heightIconProp="1.4rem" />
        </span>
      </DivRow>
    );
  };

  // Render header content (image, title, and selected text)
  const renderHeaderContent = () => (
    <DivRow>
      {imageProp != null && (
        <DivRow>
          <div className="dropdown-header-image">
            <WidgetIconFive iconProp={imageProp} widthIconProp="2rem" heightIconProp="2.1rem" />
          </div>{" "}
          <WidgetMarginWidth width="0.5rem" />
        </DivRow>
      )}
      <div>
        {title != null && (
          <WidgetRequiredLabel isRequired={isRequiredUI}>
            <ParagraphText
              className="dropdown-header-title"
              style={{
                color: titleColorProp,
                fontSize: fontSizeTitle,
                fontWeight: fontWeightTitle,
                ...(disabled && (!selectedOption || !selectedOption[labelKey]) ? getDisabledTextStyles() : {}),
              }}
            >
              {title}
            </ParagraphText>
          </WidgetRequiredLabel>
        )}
        {(selectedOption || (type === DropdownType.MULTI_SELECT && selectedOptions && selectedOptions.length > 0)) && (
          <DivColumn>
            {title != null && <WidgetMarginHeight height="0.2rem" />}
            <ParagraphText
              className="dropdown-header-text"
              style={{
                color: textColorProp,
                fontSize: fontSizeText,
                fontWeight: fontWeightText,
                fontFamily: fontFamilyText,
                lineHeight: lineHeightText,
                textAlign: textAlignText,
                ...(disabled ? getDisabledTextStyles() : {}),
              }}
            >
              {type === DropdownType.MULTI_SELECT ? getMultiSelectDisplayText() : selectedOption[labelKey]}
            </ParagraphText>
          </DivColumn>
        )}
      </div>
    </DivRow>
  );

  // Render clear button for selected items
  const renderClearButton = () => (
    <WidgetIconFour
      iconProp={ConstantsImages.CLOSE_5_IMAGE}
      widthIconProp="1.1rem"
      heightIconProp="1.2rem"
      handleOnClickProp={(e) => {
        e.stopPropagation();
        handleClear(e);
      }}
    />
  );

  // Render delete button for terminal versions that are not version 0
  const renderDeleteButton = (option) => (
    <WidgetIconFour
      iconProp={ConstantsImages.DELETE_IMAGE}
      widthIconProp="1.1rem"
      heightIconProp="1.2rem"
      handleOnClickProp={(e) => {
        e.stopPropagation();
        if (onDelete) {
          onDelete(option);
        }
      }}
    />
  );

  // Render selection indicator (checkbox or radio button)
  const renderSelectionIndicator = (option, isSelected) => {
    if (type === DropdownType.MULTI_SELECT) {
      return <WidgetCheckbox checked={isSelected} onClick={() => handleMultiSelect(option)} borderColor="#BCC6FA" />;
    }
    if (type === DropdownType.SINGLE_SELECT) {
      return <WidgetRadioButton checked={isSelected} onChange={() => handleSelect(option)} />;
    }
    return null;
  };

  // Render individual dropdown row item
  const renderDropdownRow = ({ index, style }) => {
    const option = filteredOptions[index];
    const isSelected = isOptionSelected(option);
    const showDeleteButton = shouldShowDeleteButton(option);
    const optionIsFrozen = !!option?.isFrozen;
    const usesFrozenListBranch = enableFrozenOptionDisplay && optionIsFrozen;

    return (
      <div style={style}>
        <ParagraphText
          className={`dropdown-item ${isSelected ? "selected" : ""}`}
          style={{ fontFamily: fontFamilyOption }}
          onClick={(e) => {
            e.stopPropagation();
            handleOptionClick(option);
          }}
        >
          <DivRow style={{ justifyContent: "space-between", alignItems: "center", width: "100%", gap: "0.5rem" }}>
            {usesFrozenListBranch ? renderFrozenOptionContent(option[labelKey]) : <span>{option[labelKey]}</span>}
            <DivRow style={{ alignItems: "center", gap: "0.5rem" }}>
              {renderSelectionIndicator(option, isSelected)}
              {isSelected && enableClear && type === DropdownType.DEFAULT_SELECT && renderClearButton()}
              {showDeleteButton && renderDeleteButton(option)}
            </DivRow>
          </DivRow>
        </ParagraphText>
      </div>
    );
  };

  // Render dropdown list with virtualized scrolling
  const renderDropdownList = () => {
    const ITEM_SIZE = 5;
    const MAX_HEIGHT = 15;
    const calculatedHeight = Math.min(filteredOptions.length * ITEM_SIZE, MAX_HEIGHT);

    return (
      <div className="dropdown-list" style={{ width: widthProp }}>
        <WidgetFixedSizeList listRef={listRef} height={calculatedHeight} itemCount={filteredOptions.length} itemSize={ITEM_SIZE}>
          {renderDropdownRow}
        </WidgetFixedSizeList>
      </div>
    );
  };

  // Render dropdown content
  const renderDropdownContent = () => (
    <div
      className="dropdown-content-container"
      data-portal={usePortal}
      style={
        usePortal
          ? {
              position: "fixed",
              top: `${portalPosition.top}px`,
              left: `${portalPosition.left}px`,
              width: `${portalPosition.width}px`,
              zIndex: 9999,
            }
          : {}
      }
    >
      {/* Render dropdown list */}
      {filteredOptions.length > 0 && renderDropdownList()}
      {/* Render custom footer items */}
      {(customFooterItemFirst || customFooterItemSecond) && (
        <DivColumn className="custom-footer-container">
          {customFooterItemFirst && <div onClick={onCustomItemClickFirst}>{customFooterItemFirst}</div>}
          {customFooterItemSecond && <div onClick={onCustomItemClickSecond}>{customFooterItemSecond}</div>}
        </DivColumn>
      )}
    </div>
  );

  return (
    <div className="custom-dropdown" ref={dropdownRef} style={style}>
      {/* Render dropdown header */}
      <div
        className="dropdown-header"
        style={{
          height: heightProp,
          width: widthProp,
          backgroundColor: backgroundColorProp,
          borderRadius: borderRadiusProp,
          padding: paddingProp,
          ...getDisabledStyles(),
        }}
        onClick={getDisabledClickHandler()}
      >
        {/* Render search input when dropdown is open and search is enabled */}
        {isOpen && enableSearch ? renderSearchInput() : renderHeaderContent()}
        {/* Render arrow icon */}
        {!disabled && <WidgetIconFive iconProp={arrowImage} widthIconProp={getArrowIconDimensions().width} heightIconProp={getArrowIconDimensions().height} />}
      </div>
      {/* Render dropdown list and custom footer items when dropdown is open */}
      {isOpen && (usePortal && typeof document !== "undefined" && isPortalReady ? createPortal(renderDropdownContent(), document.body) : !usePortal && renderDropdownContent())}
    </div>
  );
};

export default memo(WidgetDropdown);
